import { QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { isAbortError } from "@/lib/errors";
import { useCompanyTimezoneStore } from "@/stores/companyTimezone";

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (isAbortError(error)) return;
      // Queries that surface their own inline error states (opt out via
      // `meta: { silentError: true }`) must not spam a global toast.
      if (query.meta?.silentError) return;
      toast.error("Something went wrong", {
        description:
          error instanceof Error ? error.message : "Please try again.",
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: (failureCount, error) => {
        const httpError = error as {
          status?: number;
          response?: { status?: number };
        };
        const status = httpError.status ?? httpError.response?.status;
        if (status !== undefined && [401, 403, 429].includes(status))
          return false;
        return !isAbortError(error) && failureCount < 1;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/** Keep public requests attached to their observers during session changes. */
export function clearSessionCache(): void {
  useCompanyTimezoneStore.getState().reset();
  queryClient.removeQueries({
    predicate: (query) => query.meta?.public !== true,
  });
  queryClient.getMutationCache().clear();
}
